import * as React from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select from "@amzn/awsui-components-react/polaris/select";
import { MedicationOption } from "../add_prescription/PrescriptionDetailsBlock";
import {
  NewRxItemEntry,
  NewRxPrescriberFormState,
  NewRxMedicationFormState,
  PrescriptionIngestionStep,
  DrugSource,
} from "./NewRxItemTypes";
import NewRxPrescriberBlock from "./NewRxPrescriberBlock";
import NewRxMedicationBlock from "./NewRxMedicationBlock";

interface Props {
  entry: NewRxItemEntry;
  medications: MedicationOption[];
  onChange: (updated: NewRxItemEntry) => void;
}

const STOP_STEP_OPTIONS: { value: PrescriptionIngestionStep; label: string; description: string }[] = [
  {
    value: "RPH_APPROVAL",
    label: "RPH_APPROVAL (default)",
    description: "Ingest, then perform programmatic pharmacist approval once ready.",
  },
  {
    value: "INGEST",
    label: "INGEST",
    description: "Ingest only — stop once the message is accepted. No approval.",
  },
];

/**
 * Content for one tab = one NewRxBatchItem. Owns all mutations within the entry (prescriber,
 * drug, medication, stopAtStep) and reports the full updated entry via a single onChange
 * callback. The parent merges it back at the active tab index.
 */
class NewRxItemTabContent extends React.Component<Props> {
  private updatePrescriber = (updates: Partial<NewRxPrescriberFormState>) => {
    const { entry, onChange } = this.props;
    onChange({ ...entry, prescriber: { ...entry.prescriber, ...updates } });
  };

  private updateMedication = (updates: Partial<NewRxMedicationFormState>) => {
    const { entry, onChange } = this.props;
    onChange({ ...entry, medication: { ...entry.medication, ...updates } });
  };

  private updateDrug = (updates: { drugId: string; drugIdInput: string }) => {
    const { entry, onChange } = this.props;
    onChange({ ...entry, ...updates });
  };

  private updateDrugSource = (drugSource: DrugSource) => {
    const { entry, onChange } = this.props;
    if (drugSource === "ndc") {
      // Switching to NDC: clear any selected drug so a drugId and NDC can never coexist.
      onChange({ ...entry, drugSource, drugId: "", drugIdInput: "" });
    } else {
      // Switching to Drug ID: clear the NDC-mode NDC / UoM so the new drug selection starts clean.
      // (In Drug ID mode the user may still enter an optional NDC *override* afterward.)
      onChange({
        ...entry,
        drugSource,
        medication: { ...entry.medication, ndc: "", quantityUnitOfMeasure: "" },
      });
    }
  };

  private updateStopStep = (stopAtStep: PrescriptionIngestionStep) => {
    const { entry, onChange } = this.props;
    onChange({ ...entry, stopAtStep });
  };

  render() {
    const { entry, medications } = this.props;
    const selectedStep =
      STOP_STEP_OPTIONS.find((o) => o.value === entry.stopAtStep) || STOP_STEP_OPTIONS[0];

    return (
      <SpaceBetween size="l">
        <NewRxPrescriberBlock prescriber={entry.prescriber} onChange={this.updatePrescriber} />

        <NewRxMedicationBlock
          drugSource={entry.drugSource}
          drugId={entry.drugId}
          drugIdInput={entry.drugIdInput}
          medication={entry.medication}
          medications={medications}
          onDrugSourceChange={this.updateDrugSource}
          onDrugChange={this.updateDrug}
          onMedicationChange={this.updateMedication}
        />

        <Container header={<Header variant="h3">Stop Step</Header>}>
          <FormField
            label="Stop at step"
            description="How far to drive this prescription. Defaults to RPH_APPROVAL."
          >
            <Select
              selectedOption={{ value: selectedStep.value, label: selectedStep.label }}
              onChange={({ detail }) =>
                this.updateStopStep(detail.selectedOption.value as PrescriptionIngestionStep)
              }
              options={STOP_STEP_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                description: o.description,
              }))}
            />
          </FormField>
        </Container>
      </SpaceBetween>
    );
  }
}

export default NewRxItemTabContent;
